/* Seiko Salon turret — the homepage band's watches index upward one notch at a
   time, each watch travelling with its own reflection as a single cell.

   Design notes worth keeping (all learned the hard way, 2026-08-27):

   - The column transform is written here as a concrete percentage. A value
     containing var() is a pending-substitution value and Chrome will not
     interpolate it, so a var()-driven transform snaps instead of travelling.

   - transitionend BUBBLES. The reflections live inside the travelling column
     and carry their own hover transitions, so an unwinding hover pop fires
     transitionend on .seiko-salon__reflect, it bubbles to the column, and an
     unguarded handler settles the travel the moment it arrives — recycling the
     cell and snapping the transform to zero. Only the alcove that was hovered
     has those transitions running, which is why the bug always struck the last
     watch touched. The handler below accepts the event only when the column
     itself finished its own transform.

   - The stagger is a transition-delay, not a timer: all columns are told to
     move in the same frame and the browser holds each back by its own lag, so
     the offset cannot bunch up under a busy main thread.

   - After each move the spent cell is spliced from the front of the column to
     the back and the offset resets in the same frame with the transition off,
     so travel is always one notch in one direction and the line has no end to
     rewind from. */

(function () {
  'use strict';

  var STAGGER = 180;   // ms between alcoves — the band moves as one wave
  var RECHECK = 700;   // past the 620ms the hover pop takes to let go
  var SETTLE_PAD = 140;
  var REF_PATTERN = /\b[A-Z]{2,4}\d{2,4}[A-Z]?\b/;

  function reference(title) {
    var m = String(title || '').match(REF_PATTERN);
    return m ? m[0] : '';
  }

  function Salon(root) {
    this.root = root;
    this.band = root.querySelector('.seiko-salon__alcoves');
    if (!this.band) return;

    this.dwell = parseInt(root.dataset.dwell, 10) || 4250;
    this.travel = parseInt(root.dataset.travel, 10) || 2900;
    this.showRef = root.dataset.reference === 'true';

    this.alcoves = [];
    this.moving = false;
    this.paused = false;
    this.onScreen = false;
    this.timer = 0;
    this.imagesUpgraded = false;

    var self = this;
    Array.prototype.forEach.call(
      this.band.querySelectorAll('.seiko-salon__alcove'),
      function (el) {
        var col = el.querySelector('.seiko-salon__col');
        if (!col) return;
        var cells = col.querySelectorAll('.seiko-salon__cell');
        if (!cells.length) return;
        var state = {
          el: el,
          col: col,
          cap: el.querySelector('.seiko-salon__caption'),
          count: cells.length,
          index: 0,
          busy: false
        };
        col.style.transform = 'translateY(0)';
        self.writeCaption(state, col.children[0]);
        self.alcoves.push(state);
      }
    );
    if (!this.alcoves.length) return;

    this.onEnter = function () { self.paused = true; };
    this.onLeave = function () { self.paused = false; };
    this.band.addEventListener('pointerenter', this.onEnter);
    this.band.addEventListener('pointerleave', this.onLeave);

    // Nothing beyond the first cell is fetched until the band is approached.
    this.observer = new IntersectionObserver(function (entries) {
      self.onScreen = entries[0].isIntersecting;
      if (self.onScreen) self.upgradeImages();
    }, { rootMargin: '200px 0px', threshold: 0 });
    this.observer.observe(this.band);

    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (!this.reduced.matches && this.canRotate()) this.queue(900);
  }

  Salon.prototype.canRotate = function () {
    return this.alcoves.some(function (s) { return s.count > 1; });
  };

  /* Cells past the first are marked lazy in Liquid; once the band is near the
     viewport, let the rest fetch so the first change is never a blank frame. */
  Salon.prototype.upgradeImages = function () {
    if (this.imagesUpgraded) return;
    this.imagesUpgraded = true;
    Array.prototype.forEach.call(
      this.band.querySelectorAll('img[loading="lazy"]'),
      function (img) { img.loading = 'eager'; }
    );
  };

  Salon.prototype.writeCaption = function (state, cell) {
    if (!state.cap || !this.showRef || !cell) return;
    var ref = reference(cell.dataset.title);
    var price = cell.dataset.price || '';
    state.cap.innerHTML = ref
      ? '<b>' + ref + '</b><em>' + price + '</em>'
      : '<b>' + price + '</b>';
  };

  Salon.prototype.wave = function () {
    if (this.moving) return;
    var self = this;
    this.moving = true;
    this.band.setAttribute('data-moving', '');   // hover is inert until this clears

    this.alcoves.forEach(function (state, i) {
      self.advance(state, i * STAGGER);
    });

    var span = this.travel + (this.alcoves.length - 1) * STAGGER + SETTLE_PAD;
    setTimeout(function () {
      self.moving = false;
      self.band.removeAttribute('data-moving');
      self.queue(self.dwell);
    }, span);
  };

  Salon.prototype.advance = function (state, lag) {
    if (state.busy || state.count < 2) return;
    var self = this;
    state.busy = true;

    var next = (state.index + 1) % state.count;
    state.col.style.transitionDelay = lag + 'ms';
    state.col.style.transform = 'translateY(' + (-100 / state.count).toFixed(5) + '%)';

    if (state.cap) {
      // the arriving cell only becomes children[0] once the column recycles,
      // so name it now while it is still the one below
      var arriving = state.col.children[1] || state.col.children[0];
      state.cap.style.opacity = 0;
      setTimeout(function () {
        self.writeCaption(state, arriving);
        state.cap.style.opacity = 1;
      }, lag + self.travel * 0.5);
    }

    var done = false;
    var fallback = 0;
    function finish(e) {
      // reject anything that merely bubbled up from inside the column
      if (e && (e.target !== state.col || e.propertyName !== 'transform')) return;
      if (done) return;
      done = true;
      state.col.removeEventListener('transitionend', finish);
      clearTimeout(fallback);

      state.col.style.transition = 'none';
      state.col.style.transitionDelay = '';
      state.col.appendChild(state.col.firstElementChild);
      state.col.style.transform = 'translateY(0)';
      void state.col.offsetWidth;                // commit before motion returns
      state.col.style.transition = '';
      state.index = next;
      state.busy = false;
    }
    state.col.addEventListener('transitionend', finish);
    fallback = setTimeout(finish, lag + this.travel + 120);
  };

  Salon.prototype.queue = function (delay) {
    var self = this;
    clearTimeout(this.timer);
    this.timer = setTimeout(function () {
      if (self.paused || !self.onScreen || document.hidden) {
        self.queue(RECHECK);
        return;
      }
      self.wave();
    }, delay);
  };

  Salon.prototype.destroy = function () {
    clearTimeout(this.timer);
    if (this.observer) this.observer.disconnect();
    if (this.band) {
      this.band.removeEventListener('pointerenter', this.onEnter);
      this.band.removeEventListener('pointerleave', this.onLeave);
    }
  };

  var live = new WeakMap();

  function mount(scope) {
    var roots = (scope || document).querySelectorAll('.seiko-salon[data-salon]');
    Array.prototype.forEach.call(roots, function (root) {
      var existing = live.get(root);
      if (existing) existing.destroy();
      live.set(root, new Salon(root));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { mount(); });
  } else {
    mount();
  }

  // theme editor re-renders sections in place
  document.addEventListener('shopify:section:load', function (e) { mount(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    Array.prototype.forEach.call(
      e.target.querySelectorAll('.seiko-salon[data-salon]'),
      function (root) {
        var inst = live.get(root);
        if (inst) { inst.destroy(); live.delete(root); }
      }
    );
  });
})();
